import { useEffect, useRef } from "react";
import { bridge } from "../lib/foyerBridge";
import { getBinding, getScreenId } from "../lib/binding";
import { fetchBounded } from "../lib/boundedFetch";

/**
 * Drives guest casting on the TV, on every platform that has a receiver.
 *
 * When the operator's "Screen Share" toggle is on and the device reports the
 * `guestCasting` capability, this brings the receiver up; when the toggle is off
 * it makes sure it's torn down. What "bring up" means is per-platform and lives
 * entirely behind the bridge:
 *   - Philips MediaSuite - fetch this screen's server-minted credentials and
 *     apply them via JEDI (Secure Sharing + Google Cast on),
 *   - Samsung Tizen / LG webOS - flip their screen-mirroring receiver on; there
 *     are no credentials to fetch, so we skip the round-trip entirely,
 *   - a plain Android TV box - nothing at all: its receiver is a platform app
 *     the Screen Share tile opens directly (see `ScreenShareProvider`), not a
 *     firmware flag we own.
 *
 * The firmware persists its own config across reboots, so we read
 * {@link bridge.getCastingInfo} to stay idempotent and only call into the
 * platform when the desired state isn't already in effect. A device with no
 * receiver is a no-op - the Screen-Share page shows its instructional fallback.
 */
export function useGuestCasting(enabled: boolean): void {
  // What we last applied, so we don't re-issue native calls every render.
  const appliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!bridge.capabilities().guestCasting) return;
    // Android covers two very different devices behind one capability flag.
    // Only a MediaSuite TV has firmware flags to configure (JEDI answers
    // `supported`); a plain Android TV box simply HAS a receiver, which the
    // Screen Share tile opens directly. There is nothing to mint, apply or tear
    // down there, so we don't fire a credential round-trip - or a teardown
    // call - whose answer we would throw away.
    const configurable =
      bridge.platform !== "android" ||
      bridge.getGuestCastingStatus()?.supported === true;
    if (!configurable) return;
    let cancelled = false;

    if (!enabled) {
      if (appliedRef.current === "off") return;
      if (bridge.getCastingInfo().enabled) bridge.disableGuestCasting();
      appliedRef.current = "off";
      return;
    }

    // Platforms without a credential surface (Tizen/webOS mirroring) just get
    // switched on - there is nothing to mint or hand over.
    if (bridge.platform !== "android") {
      if (appliedRef.current === "on") return;
      if (!bridge.getCastingInfo().enabled) {
        bridge.enableGuestCasting({ ssid: "", password: "" });
      }
      appliedRef.current = "on";
      return;
    }

    void (async () => {
      const creds = await fetchGuestCastingCredentials();
      if (cancelled || !creds) return;
      const key = `on:${creds.ssid}`;
      // Wi-Fi Direct model: no SoftAP - enableGuestCasting only CONFIGURES the
      // firmware (Secure Sharing + Cast on). The receiver is launched lazily
      // from the Screen-Share page, not here. So "already live" = receiver on.
      const alreadyLive =
        appliedRef.current === key && bridge.getCastingInfo().enabled;
      if (!alreadyLive) {
        bridge.enableGuestCasting(creds);
        appliedRef.current = key;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}

export interface Credentials {
  ssid: string;
  password: string;
}

/**
 * This screen's hotspot credentials from the server, or null on any failure.
 * The server mints + persists them deterministically, so this is the single
 * source of truth for both *configuring* the SoftAP (this hook) and *displaying*
 * the join QR/SSID on the Screen-Share page - the page must NOT wait for the
 * firmware AP status to populate, which lags AP bring-up on real hardware.
 */
export async function fetchGuestCastingCredentials(): Promise<Credentials | null> {
  const binding = getBinding();
  if (!binding?.screenToken) return null;
  try {
    const res = await fetchBounded(
      `/api/frontlayer/v1/screens/${getScreenId()}/guest-casting`,
      { headers: { "X-Screen-Token": binding.screenToken } }
    );
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<Credentials>;
    if (typeof data.ssid === "string" && typeof data.password === "string") {
      return { ssid: data.ssid, password: data.password };
    }
    return null;
  } catch {
    return null;
  }
}
